import { useEffect, useState } from 'react'
import Icon, { type IconName } from './Icons'
import { api, type DeviceSummary, type EnrollTokenRow } from './api'
import { StatePill, relativeTime } from './ui'

type View = 'grid' | 'list'

/** A stat tile. `tone` colours the icon only — the number stays plain for legibility. */
function Metric({
  icon,
  tone,
  label,
  value
}: {
  icon: IconName
  tone: 'info' | 'ok' | 'accent' | 'warn' | 'muted'
  label: string
  value: number | string
}): React.JSX.Element {
  return (
    <div className="metric">
      <i className={`chip ${tone}`}>
        <Icon name={icon} />
      </i>
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  )
}

/**
 * One device as a card. The whole card is the button — a card with a small
 * "Manage" link in the corner asks people to aim at the one part of it that works.
 */
function DeviceCard({
  device: d,
  onOpen
}: {
  device: DeviceSummary
  onOpen: () => void
}): React.JSX.Element {
  return (
    <button
      className={`device-card state-${d.streamState} ${d.revoked ? 'revoked' : ''}`}
      onClick={onOpen}
    >
      <div className="dc-head">
        <span className={`dot ${d.online ? 'on' : ''}`} />
        <strong>{d.name}</strong>
        <div className="spacer" />
        <StatePill state={d.streamState} />
      </div>

      <div className="dc-meta">
        <Icon name="monitor" size={13} />
        {d.platform} · v{d.appVersion}
        {d.revoked && <span className="badge warn">revoked</span>}
      </div>

      <div className="dc-stats">
        <span title="Bitrate">
          <Icon name="activity" size={13} />
          {d.stats?.bitrateKbps ? `${Math.round(d.stats.bitrateKbps)}k` : '—'}
        </span>
        <span title="Frames per second">
          <Icon name="video" size={13} />
          {d.stats?.fps ? Math.round(d.stats.fps) : '—'}
        </span>
        <span title="Configuration version">
          <Icon name="layers" size={13} />v{d.reportedConfigVersion}
          {d.configPending && <em>→ v{d.desiredConfigVersion}</em>}
        </span>
        <span title="Last seen">
          <Icon name="clock" size={13} />
          {relativeTime(d.lastSeenAt)}
        </span>
      </div>

      {d.lastErrorText && (
        <div className="dc-error">
          <Icon name="alert" size={13} />
          {d.lastErrorText}
        </div>
      )}

      <span className="dc-go">
        <Icon name="chevronRight" size={15} />
      </span>
    </button>
  )
}

/** Fleet overview: every device, its live state, and enrollment. */
export default function DeviceList({
  onOpen
}: {
  onOpen: (id: number) => void
}): React.JSX.Element {
  // Remembered per browser: which shape suits a fleet is a property of the fleet,
  // so re-picking it on every visit would be busywork.
  const [view, setView] = useState<View>(
    () => (localStorage.getItem('yts.deviceView') as View) ?? 'grid'
  )
  const [devices, setDevices] = useState<DeviceSummary[] | null>(null)
  const [tokens, setTokens] = useState<EnrollTokenRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [note, setNote] = useState('')
  const [fresh, setFresh] = useState<{ code: string; expiresInMinutes: number } | null>(null)

  const refresh = async (): Promise<void> => {
    try {
      const [d, t] = await Promise.all([api.listDevices(), api.listEnrollTokens()])
      setDevices(d.devices)
      setTokens(t.tokens)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    void refresh()
    // Devices heartbeat every 15 s, so polling faster than that only adds load
    // without ever surfacing anything new.
    const timer = setInterval(() => void refresh(), 15000)
    return () => clearInterval(timer)
  }, [])

  const createToken = async (): Promise<void> => {
    try {
      const result = await api.createEnrollToken(note)
      setFresh(result)
      setNote('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // Pending devices are pulled out of the main list: they are a task, not fleet
  // state, and mixing them in would leave an unapproved device looking healthy.
  const pending = devices?.filter((d) => !d.approved && !d.revoked) ?? []

  const approve = async (id: number): Promise<void> => {
    try {
      await api.setDeviceApproved(id, true)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const online = devices?.filter((d) => d.online).length ?? 0
  const live = devices?.filter((d) => d.streamState === 'live').length ?? 0
  const attention = devices?.filter((d) => d.lastErrorText || d.configPending).length ?? 0

  const pick = (next: View): void => {
    setView(next)
    localStorage.setItem('yts.deviceView', next)
  }

  return (
    <div className="stack">
      <div className="metrics">
        <Metric icon="monitor" tone="info" label="Devices" value={devices?.length ?? '—'} />
        <Metric icon="power" tone="ok" label="Online" value={devices ? online : '—'} />
        <Metric icon="broadcast" tone="accent" label="Live" value={devices ? live : '—'} />
        <Metric
          icon="alert"
          tone={attention > 0 ? 'warn' : 'muted'}
          label="Needs attention"
          value={devices ? attention : '—'}
        />
      </div>

      {error && (
        <div className="banner error">
          <Icon name="alert" />
          {error}
        </div>
      )}

      {pending.length > 0 && (
        <section className="card pending-card">
          <div className="card-head">
            <h2>Waiting for approval</h2>
            <span className="badge warn">{pending.length}</span>
          </div>
          <p className="hint">
            These registered themselves and are being refused configuration and commands
            until you accept them. Check the name and platform match a device you installed
            before approving — anything that can reach the API can register.
          </p>
          {pending.map((d) => (
            <div className="pending-row" key={d.id}>
              <Icon name="monitor" size={14} />
              <div className="cam-body">
                <strong>{d.name}</strong>
                <code className="mono">
                  {d.platform} · v{d.appVersion} · first seen {relativeTime(d.createdAt)}
                </code>
              </div>
              <button className="small" onClick={() => onOpen(d.id)}>
                Inspect
              </button>
              <button className="small primary" onClick={() => void approve(d.id)}>
                <Icon name="check" />
                Approve
              </button>
            </div>
          ))}
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <h2>Devices</h2>
          <div className="spacer" />
          <div className="viewswitch">
            <button
              className={view === 'grid' ? 'on' : ''}
              title="Grid view"
              aria-pressed={view === 'grid'}
              onClick={() => pick('grid')}
            >
              <Icon name="grid" />
            </button>
            <button
              className={view === 'list' ? 'on' : ''}
              title="List view"
              aria-pressed={view === 'list'}
              onClick={() => pick('list')}
            >
              <Icon name="list" />
            </button>
          </div>
          <button onClick={() => void refresh()}>
            <Icon name="refresh" />
            Refresh
          </button>
        </div>

        {devices === null ? (
          <p className="hint">Loading…</p>
        ) : devices.length === 0 ? (
          <p className="hint">
            No devices yet. Generate an enrollment code below, then enter it in the
            streamer under <strong>Cloud</strong>.
          </p>
        ) : view === 'grid' ? (
          <div className="device-grid">
            {devices.filter((d) => d.approved || d.revoked).map((d) => (
              <DeviceCard key={d.id} device={d} onOpen={() => onOpen(d.id)} />
            ))}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="rows">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>State</th>
                  <th>Bitrate</th>
                  <th>Config</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {devices.filter((d) => d.approved || d.revoked).map((d) => (
                  /* The whole row opens the device — the old "Manage" button made a
                     40px target out of a 900px one people were already clicking. */
                  <tr
                    key={d.id}
                    className={`clickable ${d.revoked ? 'muted' : ''}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => onOpen(d.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onOpen(d.id)
                      }
                    }}
                  >
                    <td>
                      <div className="rowname">
                        <span className={`dot ${d.online ? 'on' : ''}`} />
                        <div>
                          <strong>{d.name}</strong>
                          <div className="sub">
                            {d.platform} · v{d.appVersion}
                            {d.revoked && ' · revoked'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <StatePill state={d.streamState} />
                      {d.lastErrorText && <div className="sub err">{d.lastErrorText}</div>}
                    </td>
                    <td className="mono">
                      {d.stats?.bitrateKbps ? `${Math.round(d.stats.bitrateKbps)} kbps` : '—'}
                    </td>
                    <td>
                      <span className="mono">v{d.reportedConfigVersion}</span>
                      {d.configPending && (
                        <span className="badge warn">→ v{d.desiredConfigVersion}</span>
                      )}
                    </td>
                    <td className="sub">{relativeTime(d.lastSeenAt)}</td>
                    <td className="go">
                      <Icon name="chevronRight" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Enroll a device</h2>
          <div className="spacer" />
          <button onClick={() => setEnrolling((v) => !v)}>
            <Icon name={enrolling ? 'x' : 'plus'} />
            {enrolling ? 'Close' : 'New code'}
          </button>
        </div>

        {enrolling && (
          <div className="inline">
            <input
              placeholder="Note — e.g. lobby NVR"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button className="primary" onClick={() => void createToken()}>
              Generate
            </button>
          </div>
        )}

        {fresh && (
          <div className="banner ok">
            <Icon name="key" />
            <div>
              Enter this in the streamer within {fresh.expiresInMinutes} minutes:
              <code className="code-big">{fresh.code}</code>
            </div>
          </div>
        )}

        {tokens.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Note</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className={t.usedAt || t.expired ? 'muted' : undefined}>
                    <td>
                      <code>{t.code}</code>
                    </td>
                    <td className="sub">{t.note ?? '—'}</td>
                    <td className="sub">
                      {t.usedAt
                        ? `used by device #${t.usedByDeviceId}`
                        : t.expired
                          ? 'expired'
                          : `expires ${relativeTime(t.expiresAt)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
